import * as React from 'react'

import { MobXProviderContext, observer } from 'mobx-react'

import { TableFilters } from '~/client/src/desktop/components/TableFilters'
import type DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { useTableList } from '~/client/src/desktop/tables/useTableList'
import SearchInput from '~/client/src/shared/components/Filters/SearchInput/SearchInput'
import { Loader } from '~/client/src/shared/components/Loader'
import { ViewOptionsPopover } from '~/client/src/shared/components/ViewOptions'
import { LocalStorageKey } from '~/client/src/shared/enums/LocalStorageKey'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import type SmartFormImportManagerStore from '~/client/src/shared/stores/domain/SmartFormImportManager.store'
import type WorkflowCategoriesStore from '~/client/src/shared/stores/domain/WorkflowCategories.store'

import SmartFormImportStore from './components/SmartFormImport/SmartFormImport.store'
import Header from './compound/Header'
import Modals from './compound/Modals'
import SelectionToolbar from './compound/SelectionToolbar'
import FormTypesTable from './compound/Table'
import {
  buildFormTypeFields,
  FormTypeRowActionHandlers,
  FORM_TYPE_SAVED_VIEW_ALIASES,
} from './formTypeFields'
import { useDeleteModalState } from './hooks/useDeleteModalState'
import { useFormTypeRecords } from './hooks/useFormTypeRecords'
import { FormTypeColumnId } from './types'

interface IProps {
  isUpdating: boolean
  onSelectType: (id: string) => void
  onShowInAppChange: (formTypeId: string) => void
  onAddNewFormClick: () => void
  onRemoveTypes: (typeIds: string[]) => void
}

interface ProvidedStores {
  formCategoriesStore: WorkflowCategoriesStore
  smartFormImportManagerStore: SmartFormImportManagerStore
  state: DesktopInitialState
}

function FormTypesList({
  isUpdating,
  onSelectType,
  onShowInAppChange,
  onAddNewFormClick,
  onRemoveTypes,
}: IProps): React.ReactElement {
  const { formCategoriesStore, smartFormImportManagerStore, state } =
    React.useContext(MobXProviderContext) as unknown as ProvidedStores

  const records = useFormTypeRecords()
  const modalState = useDeleteModalState()

  const smartFormImportStore = React.useMemo(
    () =>
      new SmartFormImportStore(
        formCategoriesStore,
        smartFormImportManagerStore,
      ),
    [formCategoriesStore, smartFormImportManagerStore],
  )

  const rowActionHandlers = React.useMemo<FormTypeRowActionHandlers>(
    () => ({ onEdit: onSelectType, onShowInApp: onShowInAppChange }),
    [onSelectType, onShowInAppChange],
  )
  const tableFields = React.useMemo(
    () =>
      buildFormTypeFields({
        categoryOrder: records.categoryOrder,
        rowActionHandlers,
      }),
    [records.categoryOrder, rowActionHandlers],
  )
  const tableList = useTableList({
    records: records.rows,
    schema: tableFields.schema,
    displayColumns: tableFields.columns,
    columnConfig: {
      options: tableFields.columnConfig,
      storageKey: LocalStorageKey.FormTypesColumnConfig,
      projectId: state.activeProject?.id,
      userId: state.user?.id,
    },
    selectable: true,
    selectionColumnId: FormTypeColumnId.Checkbox,
    savedViewAliases: FORM_TYPE_SAVED_VIEW_ALIASES,
    groupingAllowNone: true,
  })
  const selectedRows = tableList.selection.selectedRecords

  const canDelete =
    selectedRows.length > 0 && selectedRows.every(row => row.isDeletable)

  const handleConfirmDelete = React.useCallback(() => {
    onRemoveTypes(selectedRows.map(row => row.type))
    tableList.selection.clear()
    modalState.closeDelete()
  }, [onRemoveTypes, selectedRows, tableList.selection, modalState])

  if (records.loading) {
    return (
      <section className="tw:flex tw:h-full tw:w-full tw:flex-col tw:items-center tw:justify-center">
        <Loader hint={Localization.translator.loading} />
      </section>
    )
  }

  return (
    <section className="tw:flex tw:h-full tw:w-full tw:flex-col tw:py-4">
      <Header />

      <div className="tw:flex tw:items-center tw:gap-2 tw:border-0 tw:border-b tw:border-solid tw:border-border tw:px-4 tw:pb-2">
        <SearchInput {...tableList.searchProps} />
        <TableFilters {...tableList.filterProps} />
        <div className="tw:flex-1" />
        <ViewOptionsPopover {...tableList.viewOptionsProps} />
      </div>

      <SelectionToolbar
        list={tableList}
        canDelete={canDelete}
        onDelete={modalState.openDelete}
        smartFormImportStore={smartFormImportStore}
        onAddNewFormClick={onAddNewFormClick}
      />

      <FormTypesTable list={tableList} />

      <Modals
        isDeleteOpen={modalState.isDeleteOpen}
        isUpdating={isUpdating}
        selectedCount={selectedRows.length}
        onCancel={modalState.closeDelete}
        onConfirm={handleConfirmDelete}
      />
    </section>
  )
}

export default observer(FormTypesList)
